import { useCallback, useMemo } from 'react';

import type { ActiveFilterGroup } from '@/ui/components/ActiveFilters';

import type { FilterParams, FilterValues, Options } from './types';

const splitList = (value: string | null): string[] => (value ? value.split(',') : []);

const resetPage = (params: URLSearchParams) => {
  if (params.has('page')) {
    params.set('page', '1');
  }
};

/**
 * Derives filter form values and active-filter chips from the URL, and writes
 * them back. The URL stays the single source of truth: only the keys named in
 * `fields` are read or written, so sort, page and other params survive.
 */
const useFilterParams = ({
  fields,
  searchParams,
  setSearchParams,
  staticParams
}: Options): FilterParams => {
  const values = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fields).map(([name, field]) => [
          name,
          field.kind === 'multi'
            ? splitList(searchParams.get(name))
            : searchParams.get(name) === 'true'
        ])
      ) as FilterValues,
    [fields, searchParams]
  );

  const activeGroups = useMemo(() => {
    const groups = new Map<string, ActiveFilterGroup>();

    Object.entries(fields).forEach(([name, field]) => {
      if (field.kind === 'multi') {
        groups.set(name, {
          label: field.label,
          name,
          values: splitList(searchParams.get(name)).map((value) => ({
            label: Object.hasOwn(field.options, value) ? field.options[value] : value,
            value
          }))
        });
        return;
      }

      const group = groups.get(field.group) ?? {
        label: field.group,
        name: field.group,
        values: []
      };
      if (searchParams.get(name) === 'true') {
        group.values.push({ label: field.label, value: name });
      }
      groups.set(field.group, group);
    });

    return [...groups.values()];
  }, [fields, searchParams]);

  const apply = useCallback(
    (nextValues: FilterValues) =>
      setSearchParams((currentParams) => {
        Object.entries(fields).forEach(([name, field]) => {
          const value = nextValues[name];
          const encoded =
            field.kind === 'multi'
              ? Array.isArray(value) && value.length
                ? value.join(',')
                : null
              : value
                ? 'true'
                : null;

          if (encoded) {
            currentParams.set(name, encoded);
          } else {
            currentParams.delete(name);
          }
        });
        Object.entries(staticParams ?? {}).forEach(([key, value]) => currentParams.set(key, value));
        resetPage(currentParams);
        return currentParams;
      }),
    [fields, setSearchParams, staticParams]
  );

  const removeValue = useCallback(
    (name: string, value: string) =>
      setSearchParams((currentParams) => {
        if (fields[name]?.kind === 'multi') {
          const remaining = splitList(currentParams.get(name)).filter((item) => item !== value);
          if (remaining.length) {
            currentParams.set(name, remaining.join(','));
          } else {
            currentParams.delete(name);
          }
        } else if (fields[value]?.kind === 'flag') {
          currentParams.delete(value);
        }
        resetPage(currentParams);
        return currentParams;
      }),
    [fields, setSearchParams]
  );

  const clear = useCallback(
    () =>
      setSearchParams((currentParams) => {
        Object.keys(fields).forEach((name) => currentParams.delete(name));
        resetPage(currentParams);
        return currentParams;
      }),
    [fields, setSearchParams]
  );

  return {
    activeGroups,
    apply,
    clear,
    hasActiveFilters: activeGroups.some((group) => group.values.length),
    removeValue,
    values
  };
};

export default useFilterParams;
